import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IImportStateResponse } from 'app/modules/linkedin-import/models';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ApiService } from 'app/shared/services/base/api.service';

import { IBaseApiResponse } from 'app/shared/services/base/base-api.service';
import { createFormData } from 'app/shared/utilities/api-utilities';
import { catchError, map, Observable, throwError } from 'rxjs';
import { GetNotificationPreferencesResponse, IUserProfile, NotificationPreferenceBaseModel, PatchNotificationPreferencesRequest } from '../../models';
import { UserProfileUploadType } from '../../models/profile-upload-type';

@Injectable({
    providedIn: 'root',
})
export class ProfileApiService extends ApiService {
    constructor(http: HttpClient, appConfig: AppConfigService) {
        super(http, appConfig);
    }

    uploadLinkedInExport(file: File): Observable<IBaseApiResponse<{}>> {
        const route: string = `me/profile/linkedin-export`;
        const formData = createFormData({ file });
        return this.http.post<IBaseApiResponse<{}>>(this.url(route), formData).pipe(
            map(() => ({ success: true }) as IBaseApiResponse<{}>),
            catchError((e: HttpErrorResponse) => {
                return this.handleTypedError<IBaseApiResponse<number | string>>(e, this.appInsights, false);
            })
        );
    }

    uploadProfileImage(file: File, uploadType: UserProfileUploadType): Observable<IBaseApiResponse<{}>> {
        const route: string = `me/profile/upload?uploadType=${uploadType}`;
        const formData = createFormData({
            file,
        });
        return this.http.post<IBaseApiResponse<{}>>(this.url(route), formData).pipe(
            map((body) => {
                return { success: true } as unknown as IBaseApiResponse<{}>;
            }),
            catchError((e: HttpErrorResponse) => {
                return this.handleTypedError<IBaseApiResponse<number | string>>(e, this.appInsights, false);
            })
        );
    }

    getProfile(): Observable<IUserProfile> {
        const route: string = `me/profile`;
        return this.getObject$<IUserProfile>(route);
    }

    /** BL-413: the same IImportStateResponse shape LinkedInImportApiService.getState$ returns --
     * this is the Entra-session equivalent, reading the caller's own current attempt. */
    getLinkedInImportStatus(): Observable<IImportStateResponse | null> {
        return this.getObject$<IImportStateResponse | null>('me/profile/linkedin-import-status');
    }

    retryLinkedInImport(): Observable<IImportStateResponse | null> {
        // NOT this.post<T>() -- that returns Observable<IBaseApiResponse<number | string>>, not
        // the response body (see ApiService.post). Raw http.post instead, same shape
        // linkedin-import-api.service.ts's own retry$ already uses.
        return this.http
            .post<IImportStateResponse | null>(this.url('me/profile/linkedin-import-retry'), {})
            .pipe(catchError((e: HttpErrorResponse) => throwError(() => e)));
    }

    saveUserProfile(profile: IUserProfile): Observable<IBaseApiResponse<{}>> {
        const route: string = `me/profile`;
        return this.post<IUserProfile>(route, profile) as Observable<IBaseApiResponse<{}>>;
    }

    getNotificationPreferences(): Observable<GetNotificationPreferencesResponse> {
        const route: string = `me/profile/notification-preferences`;
        return this.getObject$<GetNotificationPreferencesResponse>(route);
    }

    updateNotificationPreferences(preferences: NotificationPreferenceBaseModel[]): Observable<boolean> {
        const body: PatchNotificationPreferencesRequest = { preferences };
        const route: string = `me/profile/notification-preferences`;
        return this.patch$(route, body, false);
    }
}
